/**
 * Detection and typed carrier for the version-conflict error
 * returned by the Helios UpdateAuditQuestionResponse endpoint.
 *
 * The mutation goes through the legacy REST client (see
 * useUpdateAuditQuestionResponseMutation for why), which throws
 * the axios response body directly. On a 409 the body carries the
 * modeled discriminator plus the extracted version fields.
 *
 * We set the wrapper's `name` to the modeled exception name so the
 * shared `resolveUserError` classifier picks it up under the same
 * rule as SDK-issued modeled exceptions — no parallel convention.
 */

/** Discriminator emitted by Helios in the 409 response body. */
const VERSION_CONFLICT_EXCEPTION_NAME = 'VersionConflictException';

export interface VersionConflictDetails {
  readonly expectedVersion: string | undefined;
  readonly actualVersion: string | undefined;
  readonly lastModifiedBy: string | undefined;
  readonly message: string | undefined;
}

/**
 * Typed Error thrown by the mutation hook on a modeled 409.
 * Callers can `if (isVersionConflictError(err))` and read the
 * extracted fields as first-class properties.
 */
export class VersionConflictError extends Error implements VersionConflictDetails {
  readonly expectedVersion: string | undefined;
  readonly actualVersion: string | undefined;
  readonly lastModifiedBy: string | undefined;
  /**
   * The server-provided message, preserved verbatim from the 409
   * body (unlike `Error.message`, which falls back to a generic
   * label when the server didn't include one). Consumers use this
   * to distinguish "server provided its own copy" from "no copy —
   * fall back to localized wording".
   */
  readonly serverMessage: string | undefined;

  constructor(details: VersionConflictDetails) {
    super(details.message ?? 'Version conflict');
    this.name = VERSION_CONFLICT_EXCEPTION_NAME;
    this.expectedVersion = details.expectedVersion;
    this.actualVersion = details.actualVersion;
    this.lastModifiedBy = details.lastModifiedBy;
    this.serverMessage = details.message;
    // Restore the prototype — required when a TS `extends Error` class
    // is compiled to a target where `super(...)` clobbers the chain.
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/** Runtime guard for consumers (page-level Flashbar, tests). */
export function isVersionConflictError(err: unknown): err is VersionConflictError {
  return err instanceof VersionConflictError;
}

/**
 * Funnels an unknown error into the typed carrier. Throws
 * `VersionConflictError` when the raw error matches the modeled
 * 409 body; rethrows the original error otherwise. Always throws —
 * never returns.
 *
 * Used from the mutation's promise chain (`.catch(...)`) so callers
 * observe the normalized error via `isVersionConflictError`.
 */
export function normalizeVersionConflictError(err: unknown): never {
  const details = detectVersionConflictInRestError(err);
  if (details) throw new VersionConflictError(details);
  throw err;
}

interface RestErrorLike {
  readonly data: {
    readonly type?: unknown;
    readonly message?: unknown;
    readonly expectedVersion?: unknown;
    readonly actualVersion?: unknown;
    readonly lastModifiedBy?: unknown;
  };
}

function hasDataObject(err: unknown): err is RestErrorLike {
  if (typeof err !== 'object' || err === null || !('data' in err)) return false;
  const data = (err as { data?: unknown }).data;
  return typeof data === 'object' && data !== null;
}

/**
 * Parses an unknown REST-layer thrown value into
 * VersionConflictDetails when the body matches, otherwise null.
 *
 * Two discriminators are accepted, mirroring V1's legacy detection:
 *   - `data.type === 'VersionConflictException'` (Smithy protocol
 *     default when the service ships the modeled name), OR
 *   - The presence of all three version-shape fields
 *     (`expectedVersion`, `actualVersion`, `lastModifiedBy` as
 *     strings) — the deployed Helios update endpoint currently
 *     returns the modeled body WITHOUT the `type` field, so this
 *     is the fallback that actually fires today.
 *
 * A bare 409 with an unrelated body still returns null.
 */
export function detectVersionConflictInRestError(
  err: unknown,
): VersionConflictDetails | null {
  if (!hasDataObject(err)) return null;
  const { data } = err;
  const hasModeledDiscriminator = data.type === VERSION_CONFLICT_EXCEPTION_NAME;
  const hasVersionShape =
    typeof data.expectedVersion === 'string' &&
    typeof data.actualVersion === 'string' &&
    typeof data.lastModifiedBy === 'string';
  if (!hasModeledDiscriminator && !hasVersionShape) return null;
  return {
    expectedVersion:
      typeof data.expectedVersion === 'string' ? data.expectedVersion : undefined,
    actualVersion:
      typeof data.actualVersion === 'string' ? data.actualVersion : undefined,
    lastModifiedBy:
      typeof data.lastModifiedBy === 'string' ? data.lastModifiedBy : undefined,
    message: typeof data.message === 'string' ? data.message : undefined,
  };
}
